import { FormItem, Input } from '@vkontakte/vkui';
import { useState, type ReactNode } from 'react';
import { api, errorMessage } from '../../api/client';
import { usePagedLoader } from '../../api/paging';
import { useSession } from '../../api/session';
import {
  CHARACTER_KIND_LABELS,
  DORM_LABELS,
  REVIEW_STATUS_LABELS,
  STUDY_CLASS_LABELS,
  type CharacterKind,
  type Dorm,
  type IsoDate,
  type Page,
  type ReviewStatus,
  type StudyClass,
} from '../../api/types';
import { t } from '../../i18n';
import { ClockIcon } from '../../shared/icons';
import { useCardScroll } from '../../shared/lib/cardScroll';
import { docNumber } from '../../shared/lib/docNumber';
import {
  Action,
  ActionRow,
  CardHeading,
  CardLink,
  CardList,
  ChipRow,
  DefList,
  Divider,
  EmptyState,
  ErrorState,
  ExternalLink,
  FieldLabel,
  FilterBar,
  FilterSelect,
  Gap,
  Gutter,
  LoadingState,
  Meta,
  Muted,
  NoticeBlock,
  Section,
  SectionTitle,
  Select,
  StatusBadge,
  toast,
  type FilterOption,
} from '../../shared/ui';
import {
  ConfirmDialog,
  DORM_OPTIONS,
  CHARACTER_KIND_OPTIONS,
  STUDY_CLASS_OPTIONS,
  ShowMore,
  Tag,
  canAct,
  formatDateTime,
  isAdmin,
  personName,
  vkProfileUrl,
} from '../review/staffKit';

/**
 * Персонажи глазами администрации: вид персонажа, общежитие, класс
 * и передача персонажа другому аккаунту.
 *
 * Общежитие и класс — админские поля: игрок их не вводит и не правит, а
 * менять их можно и вне модерации анкеты — например, при переводе между
 * общежитиями по сюжету.
 */

export interface AdminCharacter {
  id: number;
  kind: CharacterKind;
  dorm: Dorm | null;
  studyClass: StudyClass | null;
  publishedAt: string | null;
  updatedAt: string;
  deletedAt: IsoDate | null;
  deleteReason: string | null;
  owner: {
    vkUserId: number;
    displayName: string | null;
    canWrite: boolean;
  };
  nameRu: string | null;
  nameEn: string | null;
  isPublished: boolean;
  latestFormStatus: ReviewStatus | null;
  hasPendingForm: boolean;
}

type StateFilter = 'active' | 'archived';
/** none — персонажи без общежития: их анкету нельзя принять, пока оно не назначено. */
type DormFilter = 'all' | Dorm | 'none';

const STATE_FILTERS: readonly FilterOption<StateFilter>[] = [
  { value: 'active', label: t('character.admin.filter.active') },
  { value: 'archived', label: t('character.admin.filter.archived') },
];

/** Общежитие — второй срез того же списка, тем же чипом рядом с первым. */
const DORM_FILTERS: readonly FilterOption<DormFilter>[] = [
  { value: 'all', label: t('common.empty.all') },
  ...DORM_OPTIONS,
  { value: 'none', label: t('character.admin.filter.noDorm') },
];

function characterName(character: { nameRu: string | null; nameEn: string | null; id: number }) {
  return (
    character.nameRu?.trim() ||
    character.nameEn?.trim() ||
    t('character.admin.fallbackName', { no: docNumber(character.id) })
  );
}

export function AdminCharactersSection({
  active,
  tabs,
  character,
  onOpen,
  onChanged,
  onGone,
}: {
  /** Ряд разделов админки: встаёт первым в полосу фильтров этого раздела. */
  tabs: ReactNode;
  active: boolean;
  /**
   * Открытая карточка приходит СТРОКОЙ, а не идентификатором, и живёт НАРУЖИ:
   * возврат в шапке админки обязан её закрывать. Строкой — чтобы карточка не
   * зависела от выдачи списка: пока она вычислялась из него, правка статуса
   * выбрасывала строку из фильтра и карточка схлопывалась сама, а свежий
   * `updatedAt` менял ключ и пересобирал редактор вместе с его состоянием.
   * Так же устроена карточка участника по соседству.
   */
  character: AdminCharacter | null;
  onOpen: (character: AdminCharacter | null) => void;
  onChanged: (character: AdminCharacter) => void;
  onGone: () => void;
}) {
  const [state, setState] = useState<StateFilter>('active');
  const [dormFilter, setDormFilter] = useState<DormFilter>('all');

  const list = usePagedLoader<AdminCharacter>(
    (params, signal) =>
      // Тот же маршрут, что и у всех: роль — не сегмент пути. Список чужих
      // персонажей сервер отдаёт только сотруднику, остальным ответит 403.
      api.get<Page<AdminCharacter>>(
        '/api/characters',
        {
          archived: state === 'archived' ? true : undefined,
          dorm: dormFilter === 'all' || dormFilter === 'none' ? undefined : dormFilter,
          noDorm: dormFilter === 'none' ? true : undefined,
          limit: params.limit,
          offset: params.offset,
        },
        signal,
      ),
    [state, dormFilter],
    active,
  );

  const rememberScroll = useCardScroll(character !== null);

  if (character) {
    // Перечитываем через reload: он держит дочитанные страницы, а новый ключ
    // загрузчика начал бы с первой.
    return (
      <>
        <Section>{tabs}</Section>
        <CharacterEditor
          character={character}
          onChanged={(next) => {
            onChanged(next);
            list.reload();
          }}
          onGone={() => {
            onGone();
            list.reload();
          }}
        />
      </>
    );
  }

  return (
    <>
      <Section>
        {tabs}

        <FilterBar>
          <FilterSelect
            label={t('character.admin.filter.state')}
            options={STATE_FILTERS}
            value={state}
            onChange={setState}
          />
          <FilterSelect
            label={t('character.admin.filter.dorm')}
            options={DORM_FILTERS}
            value={dormFilter}
            onChange={setDormFilter}
          />
        </FilterBar>
      </Section>

      <Section>
        <SectionTitle>
          {list.data
            ? t('character.admin.listTitle', { total: list.data.total })
            : t('common.section.characters')}
        </SectionTitle>

        {list.loading && !list.data ? <LoadingState /> : null}
        {list.error ? <ErrorState message={list.error} onRetry={list.reload} /> : null}

        {list.data && list.data.items.length === 0 ? (
          <EmptyState title={t('character.admin.empty.title')}>
            {t('character.admin.empty.text')}
          </EmptyState>
        ) : null}

        <CardList>
          {list.data?.items.map((row) => (
            <CharacterRow
              key={row.id}
              character={row}
              onOpen={() => {
                rememberScroll();
                onOpen(row);
              }}
            />
          ))}
        </CardList>

        <ShowMore page={list.data} onMore={list.loadMore} loading={list.loading} />
      </Section>
    </>
  );
}

/* ── Строка списка ───────────────────────────────────────────────────────── */

/**
 * Факты идут двумя строками, а не одним subtitle: SimpleCell не переносит
 * строку, и хвост — общежитие с классом — съедало многоточием ровно там, где
 * по нему и ищут персонажа.
 *
 * Назначенное персонажу идёт ОДНИМ РЯДОМ МЕТОК: общежитие, класс,
 * «опубликована», «заявка». Общежитие и класс были строкой фактов через точку, а
 * знаки висели у имени — одно и то же о персонаже читалось с трёх ярусов.
 * Метки те же, что в его досье: общежитие залито, класс обведён малиновым.
 */
function CharacterRow({ character, onOpen }: { character: AdminCharacter; onOpen: () => void }) {
  const name = characterName(character);

  return (
    <CardLink
      label={t('character.admin.openLabel', { name })}
      tone={character.hasPendingForm ? 'pending' : 'default'}
      chevron
      onClick={onOpen}
    >
      <CardHeading>{name}</CardHeading>

      <Gap step="s1h" />
      {/* Владелец и вид персонажа — строка фактов: их читают, а не считывают
          боковым зрением. */}
      <Meta>{[personName(character.owner), CHARACTER_KIND_LABELS[character.kind]]}</Meta>

      <Gap step="s1h" />
      {/* Всё, что НАЗНАЧЕНО персонажу, — одним рядом меток: общежитие и класс
          ставит модератор, «опубликована» и «заявка» говорят, где персонаж
          сейчас. Порознь, строкой фактов и знаком у имени, это собиралось
          глазами по трём ярусам. Метки те же, что в досье: общежитие залито
          (принадлежность), класс обведён малиновым (ранг). */}
      <ChipRow>
        {character.dorm === null ? (
          <Tag>{t('character.facts.dormless')}</Tag>
        ) : (
          <Tag tone="success" fill>
            {DORM_LABELS[character.dorm]}
          </Tag>
        )}
        {character.studyClass === null ? (
          <Tag>{t('character.facts.classUnassigned')}</Tag>
        ) : (
          <Tag tone="error">
            {t('character.facts.studyClass', { value: STUDY_CLASS_LABELS[character.studyClass] })}
          </Tag>
        )}
        {character.isPublished ? (
          <StatusBadge tone="published">{t('character.mark.published')}</StatusBadge>
        ) : (
          <StatusBadge tone="archived">{t('character.badge.unpublished')}</StatusBadge>
        )}
        {character.hasPendingForm ? (
          // Часы перед подписью: очередь видно боковым зрением раньше, чем
          // прочитано слово. Так набрано в холсте.
          <StatusBadge tone="pending">
            <ClockIcon size={9} />
            {t('character.badge.request')}
          </StatusBadge>
        ) : null}
      </ChipRow>
    </CardLink>
  );
}

/* ── Карточка персонажа ──────────────────────────────────────────────────── */

function CharacterEditor({
  character,
  onChanged,
  onGone,
}: {
  character: AdminCharacter;
  /** Свежая строка после правки: родитель кладёт её к себе, редактор не пересобирается. */
  onChanged: (character: AdminCharacter) => void;
  /** Персонажа в карточке больше нет — передан или удалён: карточка закрывается, список перечитывается. */
  onGone: () => void;
}) {
  const session = useSession();
  const writable = canAct(session);
  const admin = isAdmin(session);

  const [kind, setKind] = useState<CharacterKind>(character.kind);
  const [dorm, setDorm] = useState<Dorm | ''>(character.dorm ?? '');
  const [studyClass, setStudyClass] = useState<StudyClass | ''>(character.studyClass ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const [transferTo, setTransferTo] = useState('');
  const [reason, setReason] = useState('');
  const [archiveReason, setArchiveReason] = useState('');
  const [confirming, setConfirming] = useState<'transfer' | 'archive' | 'purge' | null>(null);

  const archived = character.deletedAt !== null;

  const numeric = /^\d+$/.test(transferTo.trim());

  const dirty =
    kind !== character.kind ||
    dorm !== (character.dorm ?? '') ||
    studyClass !== (character.studyClass ?? '');

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const updated = await api.patch<Partial<AdminCharacter>>(`/api/characters/${character.id}`, {
        kind,
        ...(dorm ? { dorm } : {}),
        ...(studyClass ? { studyClass } : {}),
      });
      // Свежую строку кладём НА МЕСТО, а не ждём её из перечитанного списка:
      // иначе редактор пересобрался бы и унёс с собой это подтверждение.
      onChanged({ ...character, ...updated });
      setSaved(t('common.saved'));
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function transfer() {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      await api.post(`/api/characters/${character.id}/transfer`, {
        vkUserId: Number(transferTo.trim()),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      setConfirming(null);
      setTransferTo('');
      setReason('');
      toast(t('character.admin.transferred'));
      // Персонаж больше не принадлежит тому, чью карточку открыли: показывать
      // прежнего владельца нельзя, а нового сервер в ответе не называет.
      onGone();
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const trimmed = archiveReason.trim();
      await api.delete(`/api/characters/${character.id}`, trimmed ? { reason: trimmed } : {});
      setConfirming(null);
      setArchiveReason('');
      // Сервер отвечает одним `ok`, поэтому дату ставим своими часами: до
      // перечитывания списка она расходится с базой на доли секунды.
      onChanged({
        ...character,
        deletedAt: new Date().toISOString(),
        deleteReason: trimmed || null,
      });
      setSaved(t('character.admin.archivedDone'));
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function purge() {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      await api.delete(`/api/characters/${character.id}/purge`);
      setConfirming(null);
      toast(t('character.admin.purged'));
      onGone();
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  const dialogs = {
    transfer: {
      title: t('character.admin.confirm.transfer.title'),
      text: t('character.admin.confirm.transfer.text', { id: transferTo.trim() }),
      confirmText: t('character.admin.confirm.transfer.confirm'),
      destructive: false,
      run: transfer,
    },
    archive: {
      title: t('character.admin.confirm.archive.title'),
      text: t('character.admin.confirm.archive.text'),
      confirmText: t('character.admin.confirm.archive.confirm'),
      destructive: false,
      run: archive,
    },
    purge: {
      title: t('character.admin.confirm.purge.title'),
      text: t('character.admin.confirm.purge.text'),
      confirmText: t('character.admin.confirm.purge.confirm'),
      destructive: true,
      run: purge,
    },
  };
  const dialog = confirming === null ? null : dialogs[confirming];

  return (
    <>
      <Section>
        <Gutter>
          <CardHeading size="l">{characterName(character)}</CardHeading>
          <Gap step="s2h" />
          {/* Ряд, а не голые метки подряд: у ряда свой шаг между ними, иначе
              они держатся только на пробелах в разметке. */}
          <ChipRow>
            <Tag>{CHARACTER_KIND_LABELS[character.kind]}</Tag>
            {archived ? <Tag>{t('common.state.archived')}</Tag> : null}
            {character.isPublished ? (
              <Tag tone="success">{t('character.mark.published')}</Tag>
            ) : (
              <Tag tone="warning">{t('character.badge.unpublished')}</Tag>
            )}
            {character.hasPendingForm ? (
              <Tag tone="warning">{t('character.badge.requestPending')}</Tag>
            ) : null}
          </ChipRow>
        </Gutter>

        <Divider />

        <Gutter>
          <DefList
            items={[
              {
                label: t('character.admin.field.owner'),
                value: (
                  <>
                    <ExternalLink href={vkProfileUrl(character.owner.vkUserId)}>
                      {personName(character.owner)}
                    </ExternalLink>
                    {character.owner.canWrite ? '' : t('character.admin.accessRevoked')}
                  </>
                ),
              },
              {
                label: t('character.admin.field.formStatus'),
                value: character.latestFormStatus
                  ? REVIEW_STATUS_LABELS[character.latestFormStatus]
                  : t('character.admin.noForm'),
              },
              {
                label: t('character.admin.field.publishedAt'),
                value: formatDateTime(character.publishedAt),
              },
              ...(archived
                ? [
                    {
                      label: t('character.admin.field.archivedAt'),
                      value: formatDateTime(character.deletedAt),
                    },
                    {
                      label: t('character.admin.field.archiveReason'),
                      value: character.deleteReason ?? t('character.admin.noArchiveReason'),
                    },
                  ]
                : []),
            ]}
          />
        </Gutter>
      </Section>

      {saved ? <NoticeBlock tone="success">{saved}</NoticeBlock> : null}
      {error ? (
        <NoticeBlock tone="error" title={t('character.admin.error.title')}>
          {error}
        </NoticeBlock>
      ) : null}

      <Section>
        <SectionTitle>{t('character.admin.section.fields')}</SectionTitle>

        <FormItem
          top={
            <FieldLabel hint={t('character.admin.hint.kind')}>
              {t('character.admin.field.kind')}
            </FieldLabel>
          }
        >
          <Select
            value={kind}
            disabled={busy || !writable}
            options={CHARACTER_KIND_OPTIONS}
            onChange={(event) => setKind(event.target.value as CharacterKind)}
          />
        </FormItem>

        <FormItem
          top={
            <FieldLabel hint={t('character.admin.hint.dorm')}>
              {t('character.admin.field.dorm')}
            </FieldLabel>
          }
        >
          <Select
            value={dorm}
            disabled={busy || !writable}
            placeholder={t('character.admin.placeholder.dormEmpty')}
            options={DORM_OPTIONS}
            onChange={(event) => setDorm(event.target.value as Dorm | '')}
          />
        </FormItem>

        <FormItem
          top={
            <FieldLabel hint={t('character.admin.hint.studyClass')}>
              {t('character.admin.field.studyClass')}
            </FieldLabel>
          }
        >
          <Select
            value={studyClass}
            disabled={busy || !writable}
            placeholder={t('character.admin.placeholder.classEmpty')}
            options={STUDY_CLASS_OPTIONS}
            onChange={(event) => setStudyClass(event.target.value as StudyClass | '')}
          />
        </FormItem>

        <Gutter>
          <Action
            tone="accent"
            size="m"
            stretched
            loading={busy}
            disabled={busy || !writable || !dirty}
            onClick={() => void save()}
          >
            {t('common.action.save')}
          </Action>
          <Gap step="s2" />
          <Muted>{t('character.admin.hint.cantUnset')}</Muted>
        </Gutter>
      </Section>

      {admin ? (
        <Section>
          <SectionTitle accent="pink">{t('character.admin.section.transfer')}</SectionTitle>

          <FormItem
            top={
              <FieldLabel hint={t('character.admin.hint.transferTo')}>
                {t('character.admin.field.transferTo')}
              </FieldLabel>
            }
            status={transferTo && !numeric ? 'error' : 'default'}
            bottom={transferTo && !numeric ? t('character.admin.error.digitsOnly') : undefined}
          >
            <Input
              value={transferTo}
              inputMode="numeric"
              placeholder={t('character.admin.placeholder.vkId')}
              disabled={busy}
              onChange={(event) => setTransferTo(event.target.value.replace(/\D/g, ''))}
            />
          </FormItem>

          <FormItem
            top={
              <FieldLabel hint={t('character.admin.hint.reason')}>
                {t('character.admin.field.reason')}
              </FieldLabel>
            }
          >
            <Input
              value={reason}
              disabled={busy}
              placeholder={t('character.admin.placeholder.reason')}
              onChange={(event) => setReason(event.target.value)}
            />
          </FormItem>

          <Gutter>
            <Action
              tone="action"
              size="m"
              stretched
              disabled={busy || !writable || !numeric}
              onClick={() => setConfirming('transfer')}
            >
              {t('character.admin.action.transfer')}
            </Action>
          </Gutter>
        </Section>
      ) : null}

      {admin ? (
        <Section>
          <SectionTitle accent="pink">{t('character.admin.section.archive')}</SectionTitle>

          {archived ? null : (
            <FormItem
              top={
                <FieldLabel hint={t('character.admin.hint.archiveReason')}>
                  {t('character.admin.field.reason')}
                </FieldLabel>
              }
            >
              <Input
                value={archiveReason}
                disabled={busy}
                placeholder={t('character.admin.placeholder.archiveReason')}
                onChange={(event) => setArchiveReason(event.target.value)}
              />
            </FormItem>
          )}

          <Gutter>
            <ActionRow>
              {archived ? null : (
                <Action
                  tone="quiet"
                  size="m"
                  stretched
                  disabled={busy || !writable}
                  onClick={() => setConfirming('archive')}
                >
                  {t('character.admin.action.archive')}
                </Action>
              )}
              <Action
                tone="danger"
                size="m"
                stretched
                disabled={busy || !writable}
                onClick={() => setConfirming('purge')}
              >
                {t('character.admin.action.purge')}
              </Action>
            </ActionRow>
          </Gutter>
        </Section>
      ) : null}

      {dialog ? (
        <ConfirmDialog
          title={dialog.title}
          text={dialog.text}
          confirmText={dialog.confirmText}
          destructive={dialog.destructive}
          busy={busy}
          error={error}
          onCancel={() => setConfirming(null)}
          onConfirm={() => void dialog.run()}
        />
      ) : null}
    </>
  );
}
